import { useDispatch } from "react-redux";

import { setHasViewItemsChanged } from "store/features";
import { useMapStore } from "stores/map";

import { useClearFilter } from "components/project/filters/hooks/useClearFilter/useClearFilter";
import { useClearForecasts } from "components/project/forecasts/hooks/useClearForecasts";
import { useClearTypewells } from "components/project/forecasts/hooks/useClearTypeWells";
import { useClearLayers } from "components/project/layers/hooks";
import { useProjectDispatch } from "components/project/projects/context";

interface IClearProjectItems {
  // For the purpose of not overriding filter defaults, as this is used in initial project loading
  // If it gets more complex we can just make a different purposed hook
  isClearingFilter?: boolean;
}

interface ClearViewOptions {
  // When true, the checked layers snap back to the defaults instead of the
  // last session selection. Used when deleting a view.
  resetLayersToDefault?: boolean;
}

export function useClearProjectItems(
  params: IClearProjectItems = { isClearingFilter: true }
) {
  const dispatch = useDispatch();
  const projectDispatch = useProjectDispatch();

  const clearLayers = useClearLayers();
  const clearForecasts = useClearForecasts();
  const clearTypeWells = useClearTypewells();
  const clearFilter = useClearFilter();

  function clearView(nextProjectId: string, options: ClearViewOptions = {}) {
    clearLayers.update(nextProjectId, {
      resetToDefaults: options.resetLayersToDefault
    });
    clearTypeWells.update();
    clearForecasts.update();

    if (params.isClearingFilter) {
      clearFilter.update(nextProjectId);
    }

    // Deleting or clearing the selected view leaves no view active, so the
    // rig/frac layer settings (filters, hidden values, colour source, icon
    // sizes) must fall back to their defaults instead of lingering from the
    // just-removed view. Passing no saved settings resets exactly that subset
    // (see applyViewDisplaySettings); non-rig/frac settings are left as-is.
    useMapStore.getState().applyViewDisplaySettings(undefined);

    projectDispatch({
      payload: {
        selectedViewId: undefined
      }
    });

    dispatch(setHasViewItemsChanged(false));
  }

  return { update: clearView };
}
